<template>
  <div>
    <v-dialog v-model="dialogOpen" max-width="640" scrollable persistent>
      <v-card class="guestbook-dialog" :dark="isDarkMode">
        <div class="guestbook-hero">
          <div class="d-flex align-start justify-space-between">
            <div class="d-flex align-center" style="min-width: 0">
              <div class="guestbook-hero__icon mr-3">
                <v-icon color="teal darken-1" size="24">mdi-book-open-page-variant-outline</v-icon>
              </div>
              <div style="min-width: 0">
                <div class="guestbook-eyebrow">{{ sourceLabel }}</div>
                <h2 class="guestbook-title">Neuer Gästebucheintrag</h2>
                <div class="guestbook-time">
                  <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                  {{ submittedLabel }}
                </div>
              </div>
            </div>
            <v-btn icon :disabled="linking" aria-label="Gästebucheintrag schliessen" @click.stop="close">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <v-card-text class="guestbook-content pa-5 pa-md-6">
          <section class="guestbook-person mb-5">
            <v-avatar size="48" class="guestbook-avatar mr-3">
              <span>{{ initials }}</span>
            </v-avatar>
            <div style="min-width: 0">
              <div class="guestbook-person__name guestbook-text">{{ fullName }}</div>
              <div class="d-flex flex-wrap mt-2" style="gap: 8px">
                <v-chip v-if="safeEntry.email" small outlined color="teal" :href="`mailto:${safeEntry.email}`">
                  <v-icon left small>mdi-email-outline</v-icon>{{ safeEntry.email }}
                </v-chip>
                <v-chip v-if="safeEntry.phone" small outlined color="teal" :href="`tel:${safeEntry.phone}`">
                  <v-icon left small>mdi-phone-outline</v-icon>{{ safeEntry.phone }}
                </v-chip>
              </div>
            </div>
          </section>

          <div class="guestbook-assignment mb-5">
            <div v-if="handoffTrainerName" class="guestbook-assignment__row">
              <v-icon size="20" class="guestbook-assignment__icon">mdi-account-arrow-right-outline</v-icon>
              <div>
                <div class="guestbook-assignment__label">Gästebuch übergeben von</div>
                <div class="guestbook-assignment__name">{{ handoffTrainerName }}</div>
              </div>
            </div>
            <div class="guestbook-assignment__row guestbook-assignment__row--target">
              <v-icon size="20" color="teal darken-1" class="guestbook-assignment__icon">mdi-calendar-account-outline</v-icon>
              <div>
                <div class="guestbook-assignment__label">Termin wird hinterlegt bei</div>
                <div class="guestbook-assignment__name">{{ appointmentTrainerLabel }}</div>
              </div>
            </div>
          </div>

          <div class="guestbook-detail-grid mb-5">
            <div class="guestbook-detail-card">
              <div class="guestbook-detail-card__label">
                <v-icon small class="mr-1">mdi-bullhorn-outline</v-icon>Aufmerksam geworden durch
              </div>
              <div class="guestbook-detail-card__value">{{ referralLabel || "Keine Angabe" }}</div>
            </div>
            <div class="guestbook-detail-card">
              <div class="guestbook-detail-card__label">
                <v-icon small class="mr-1">mdi-target</v-icon>Trainingsziele
              </div>
              <div v-if="goalLabels.length" class="d-flex flex-wrap" style="gap: 6px">
                <v-chip v-for="goal in goalLabels" :key="goal" small outlined color="teal">
                  {{ goal }}
                </v-chip>
              </div>
              <div v-else class="guestbook-detail-card__value">Keine Angabe</div>
            </div>
          </div>

          <v-divider class="mb-5" />

          <section>
            <div class="d-flex align-center mb-4">
              <div class="guestbook-section-icon mr-3">
                <v-icon color="teal darken-1">mdi-account-convert-outline</v-icon>
              </div>
              <div>
                <div class="text-subtitle-1 font-weight-bold guestbook-text">Termin übernehmen</div>
                <div class="caption grey--text">{{ appointmentDestinationText }}</div>
              </div>
            </div>

            <v-alert v-if="!canManageAccount" text dense type="info" class="mb-0">
              Zum Erstellen oder Verknüpfen eines Kundenaccounts fehlt dir die entsprechende Kundenberechtigung.
            </v-alert>

            <div v-else-if="linkedCustomer" class="guestbook-linked-panel pa-4">
              <div class="d-flex align-center mb-4">
                <v-avatar size="42" color="teal" class="white--text font-weight-bold mr-3">{{ linkedCustomerInitials }}</v-avatar>
                <div style="min-width: 0">
                  <div class="font-weight-bold guestbook-text text-truncate">{{ linkedCustomerName }}</div>
                  <div class="caption grey--text text-truncate">{{ linkedCustomer.email || "Account ist bereits mit diesem Lead verbunden" }}</div>
                </div>
              </div>
              <v-btn block large depressed color="teal darken-1" class="white--text font-weight-bold" :loading="linking" @click="confirmExistingLink">
                <v-icon left>mdi-calendar-account-outline</v-icon>Als Kundentermin übernehmen
              </v-btn>
            </div>

            <div v-else-if="guestStep === 'type'" class="guestbook-search-panel pa-4">
              <div class="font-weight-bold guestbook-text mb-1">Welche Terminart möchtest du buchen?</div>
              <div class="caption grey--text mb-4">Danach wählst du im Terminfinder einen freien Termin und den Trainer.</div>
              <v-btn block large outlined color="teal darken-1" class="font-weight-bold mb-3" @click="openGuestFinder('trial')">
                <v-icon left>mdi-account-star</v-icon>Probetraining · 60 Min.
              </v-btn>
              <v-btn block large outlined color="teal darken-1" class="font-weight-bold" @click="openGuestFinder('consultation')">
                <v-icon left>mdi-account-tie</v-icon>Beratungstermin · 60 Min.
              </v-btn>
              <v-btn text small color="grey" class="mt-3" @click="guestStep = ''">Zurück</v-btn>
            </div>

            <template v-else>
              <div v-if="!showSearch">
                <v-btn
                  v-if="!safeEntry.roodyUser"
                  block
                  large
                  outlined
                  color="teal darken-1"
                  class="font-weight-bold mb-2"
                  :loading="linking"
                  :disabled="showRegisterDialog"
                  @click="startGuestAppointment">
                  <v-icon left>mdi-calendar-account-outline</v-icon>Gasttermin ohne Account
                </v-btn>
                <div v-if="!safeEntry.roodyUser" class="caption grey--text mb-4">
                  Terminart und freien Termin auswählen. Die Gastdaten werden übernommen; den Account kannst du später erstellen.
                </div>
                <v-btn
                  v-if="canCreateAccount"
                  block
                  large
                  depressed
                  color="teal darken-1"
                  class="white--text font-weight-bold mb-3"
                  :disabled="linking"
                  @click="openRegistration">
                  <v-icon left>mdi-account-plus-outline</v-icon>Account erstellen
                </v-btn>
                <v-btn v-if="canLinkAccount" block large outlined color="teal darken-1" class="font-weight-bold" :disabled="linking" @click="showSearch = true">
                  <v-icon left>mdi-link-variant</v-icon>Mit bestehendem Account verknüpfen
                </v-btn>
              </div>

              <div v-else class="guestbook-search-panel pa-4">
                <div class="font-weight-bold guestbook-text mb-1">Bestehenden Kunden suchen</div>
                <div class="caption grey--text mb-3">Suche nach Name oder E-Mail-Adresse und wähle den passenden Account.</div>
                <user-search
                  return-user
                  ignore-rights
                  :lookup-method="searchGuestbookCustomers"
                  label="Kunde suchen…"
                  :dark="isDarkMode"
                  :disabled="linking"
                  @update="linkCustomer" />
                <div class="d-flex justify-end">
                  <v-btn text small color="grey" :disabled="linking" @click="showSearch = false">Abbrechen</v-btn>
                </div>
              </div>
            </template>
          </section>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog :value="guestStep === 'finder'" fullscreen persistent transition="dialog-bottom-transition">
      <v-card class="guestbook-finder" :dark="isDarkMode">
        <div class="guestbook-finder__header pa-4 d-flex align-center">
          <v-btn icon :disabled="linking" aria-label="Zurück zur Terminart" class="mr-2" @click="backToGuestType">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div style="min-width: 0" class="flex-grow-1">
            <div class="text-h6 font-weight-bold">{{ guestAppointmentTitle }} · 60 Min.</div>
            <div class="text-body-2 text-truncate">{{ fullName }} · Gast ohne Account</div>
          </div>
          <v-btn icon :disabled="linking" aria-label="Terminsuche abbrechen" @click="cancelGuestAppointment">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-alert v-if="guestBookingError" dense text type="warning" class="mx-4 mb-2">{{ guestBookingError }}</v-alert>
        <work-calendar-appointment-finder
          v-if="guestStep === 'finder'"
          ref="guestAppointmentFinder"
          class="guestbook-finder__body"
          :class="{ 'guestbook-finder__body--saving': linking }"
          :employees="guestFinderEmployees"
          :date="guestFinderDate"
          :start="guestFinderStart"
          :col-id="guestFinderEmployeeId"
          :duration="60"
          :is-dark-mode="isDarkMode"
          :saving="linking"
          :confirm-label="guestAppointmentTitle + ' buchen'"
          @update:date="guestFinderDate = $event"
          @update:start="guestFinderStart = $event"
          @update:col-id="guestFinderEmployeeId = $event"
          @select-slot="selectGuestSlot"
          @clear-selection="guestFinderSelection = null"
          @select-and-close="createGuestAppointment" />
      </v-card>
    </v-dialog>

    <register
      :active="showRegisterDialog"
      :auto-login="false"
      :require-password="false"
      :initial-data="registrationPrefill"
      reset-on-open
      @register="newAccountRegistered"
      @close="closeRegistration" />
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import UserSearch from "@/components/userSearch.vue"
import register from "@/views/launchScreen/register.vue"
import WorkCalendarAppointmentFinder from "./WorkCalendarAppointmentFinder.vue"

const GOAL_LABELS = {
  weight_loss: "Abnehmen",
  muscle_gain: "Muskelaufbau",
  toning: "Straffen",
  endurance: "Ausdauer verbessern",
  back_strength: "Rücken kräftigen",
  pain_relief: "Schmerzen beseitigen",
  movement: "Bewegungsdrang ausleben",
  mobility: "Beweglichkeit verbessern",
  coordination: "Koordination verbessern",
  health: "Gesund bleiben",
  posture: "Haltung verbessern",
  rehabilitation: "Rehabilitation",
  competition: "Wettkampfvorbereitung",
  other: "Anderes Ziel",
}

export default {
  name: "WorkCalendarGuestbookDialog",
  components: { UserSearch, register, WorkCalendarAppointmentFinder },
  props: {
    value: { type: Boolean, default: false },
    entry: { type: Object, default: () => ({}) },
    isDarkMode: { type: Boolean, default: false },
    sourceLabel: { type: String, default: "Website · Gästebuch" },
    assignToCurrentUser: { type: Boolean, default: false },
    assignedEmployeeId: { type: String, default: "" },
    assignedEmployeeName: { type: String, default: "" },
    confirmAppointmentCreation: { type: Function, default: () => true },
  },
  data() {
    return {
      userService,
      linking: false,
      showSearch: false,
      showRegisterDialog: false,
      pendingRegistrationEntry: null,
      guestStep: "",
      guestAppointmentType: "",
      guestFinderDate: "",
      guestFinderStart: "",
      guestFinderEmployeeId: "",
      guestFinderSelection: null,
      guestFinderEmployees: Object.freeze([]),
      guestBookingError: "",
      guestBookingRequestId: 0,
    }
  },
  computed: {
    safeEntry() {
      return this.entry || {}
    },
    dialogOpen: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit("input", value)
      },
    },
    entryVersionKey() {
      const submittedAt = this.safeEntry.walkInCalendar && this.safeEntry.walkInCalendar.submittedAt
      return `${this.safeEntry._id || ""}:${submittedAt || ""}`
    },
    canManageAccount() {
      return this.canCreateAccount || this.canLinkAccount
    },
    canCreateAccount() {
      return userService.hasRight("ab_create", "c_create_customer", "c_create")
    },
    canLinkAccount() {
      return userService.hasRight("ab_create", "u_search")
    },
    handoffTrainerName() {
      return this.safeEntry.walkInCalendar?.handedOverByName || ""
    },
    appointmentTrainerId() {
      return String(this.assignedEmployeeId || "")
    },
    appointmentTrainerName() {
      return this.assignedEmployeeName || ""
    },
    appointmentTrainerLabel() {
      if (this.appointmentTrainerId) return this.appointmentTrainerName || "Ausgewählter Trainer"
      return this.assignToCurrentUser ? "Dir" : "MA Info"
    },
    appointmentDestinationText() {
      return "Mit Kundenaccount öffnet sich danach die Anamnese."
    },
    fullName() {
      return `${this.safeEntry.name || ""} ${this.safeEntry.surname || ""}`.trim() || "Unbekannter Gast"
    },
    initials() {
      return `${String(this.safeEntry.name || "").charAt(0)}${String(this.safeEntry.surname || "").charAt(0)}`.toUpperCase() || "G"
    },
    submittedLabel() {
      const submittedAt = this.safeEntry.walkInCalendar && this.safeEntry.walkInCalendar.submittedAt
      if (submittedAt) return this.$moment(submittedAt).format("DD.MM.YYYY, HH:mm")
      const date = this.safeEntry.walkInCalendar && this.safeEntry.walkInCalendar.date
      const time = this.safeEntry.walkInCalendar && this.safeEntry.walkInCalendar.time
      return [date ? this.$moment(date).format("DD.MM.YYYY") : "", time].filter(Boolean).join(", ")
    },
    referralLabel() {
      if (!this.safeEntry.referralSource) return ""
      if (this.safeEntry.referralSource === "Andere" && this.safeEntry.referralSourceOther) return `Andere: ${this.safeEntry.referralSourceOther}`
      return this.safeEntry.referralSource
    },
    goalLabels() {
      return (this.safeEntry.goals || []).map((goal) => {
        if (goal === "other" && this.safeEntry.goalOther) return `Anderes Ziel: ${this.safeEntry.goalOther}`
        return GOAL_LABELS[goal] || goal
      })
    },
    linkedCustomer() {
      return this.safeEntry.roodyUser && typeof this.safeEntry.roodyUser === "object" ? this.safeEntry.roodyUser : null
    },
    linkedCustomerName() {
      if (!this.linkedCustomer) return "Verknüpfter Account"
      return `${this.linkedCustomer.vorname || ""} ${this.linkedCustomer.nachname || ""}`.trim() || "Verknüpfter Account"
    },
    linkedCustomerInitials() {
      if (!this.linkedCustomer) return "K"
      return `${String(this.linkedCustomer.vorname || "").charAt(0)}${String(this.linkedCustomer.nachname || "").charAt(0)}`.toUpperCase() || "K"
    },
    guestAppointmentTitle() {
      return this.guestAppointmentType === "consultation" ? "Beratungstermin" : "Probetraining"
    },
    registrationPrefill() {
      return {
        email: this.safeEntry.email || "",
        vorname: this.safeEntry.name || "",
        nachname: this.safeEntry.surname || "",
        telefon: this.safeEntry.phone || "",
      }
    },
  },
  watch: {
    value(value) {
      if (!value) this.resetLocalState()
    },
    entryVersionKey(value, previousValue) {
      if (value !== previousValue) this.resetLocalState()
    },
  },
  methods: {
    searchGuestbookCustomers(search, signal) {
      return api.shiftManagement.searchGuestbookCustomers(search, signal)
    },
    resetLocalState() {
      this.linking = false
      this.showSearch = false
      this.showRegisterDialog = false
      this.pendingRegistrationEntry = null
      this.guestStep = ""
      this.guestAppointmentType = ""
      this.guestFinderDate = ""
      this.guestFinderStart = ""
      this.guestFinderEmployeeId = ""
      this.guestFinderSelection = null
      this.guestBookingError = ""
      this.guestBookingRequestId += 1
    },
    close() {
      if (this.linking) return
      this.dialogOpen = false
    },
    openRegistration() {
      const walkInCalendar = this.safeEntry.walkInCalendar || {}
      this.pendingRegistrationEntry = {
        _id: this.safeEntry._id,
        name: this.safeEntry.name,
        surname: this.safeEntry.surname,
        appointmentAssignment: {
          employeeId: this.appointmentTrainerId,
          employeeName: this.appointmentTrainerName,
          assignToCurrentUser: this.assignToCurrentUser,
        },
        walkInCalendar: {
          submittedAt: walkInCalendar.submittedAt,
          date: walkInCalendar.date,
          time: walkInCalendar.time,
          appointment: walkInCalendar.appointment,
          handedOverBy: walkInCalendar.handedOverBy,
          handedOverByName: walkInCalendar.handedOverByName,
        },
      }
      this.showRegisterDialog = true
    },
    closeRegistration() {
      this.showRegisterDialog = false
      this.pendingRegistrationEntry = null
    },
    async confirmExistingLink() {
      if (!this.linkedCustomer || !this.linkedCustomer._id) return
      await this.linkCustomer(this.linkedCustomer)
    },
    startGuestAppointment() {
      if (!this.canManageAccount || this.safeEntry.roodyUser || this.linking) return
      this.guestBookingError = ""
      this.guestStep = "type"
    },
    openGuestFinder(appointmentType) {
      if (!this.canManageAccount || this.safeEntry.roodyUser || this.linking || !["trial", "consultation"].includes(appointmentType)) return
      const today = this.$moment().format("YYYY-MM-DD")
      const entryDate = this.safeEntry.walkInCalendar?.date
      this.guestAppointmentType = appointmentType
      this.guestFinderDate = this.$moment(entryDate, "YYYY-MM-DD", true).isValid() && entryDate > today ? entryDate : today
      this.guestFinderStart = ""
      this.guestFinderEmployeeId = this.appointmentTrainerId
      this.guestFinderSelection = null
      this.guestBookingError = ""
      this.guestStep = "finder"
    },
    backToGuestType() {
      if (this.linking) return
      this.guestFinderSelection = null
      this.guestBookingError = ""
      this.guestStep = "type"
    },
    cancelGuestAppointment() {
      if (this.linking) return
      this.guestFinderSelection = null
      this.guestBookingError = ""
      this.guestStep = ""
    },
    selectGuestSlot(selection) {
      if (this.linking || !selection) return
      this.guestFinderSelection = { ...selection }
      this.guestFinderDate = selection.date
      this.guestFinderStart = selection.start
      this.guestFinderEmployeeId = String(selection.colId || "")
      this.guestBookingError = ""
    },
    async createGuestAppointment(selection) {
      if (!this.canManageAccount || this.safeEntry.roodyUser || this.linking || this.guestStep !== "finder") return
      const selected = this.guestFinderSelection
      if (!selection || !selected || !["trial", "consultation"].includes(this.guestAppointmentType) ||
        !selection.colId || ["ma_info", "no_show", "unassigned", "none"].includes(String(selection.colId)) ||
        ["date", "start", "end", "colId"].some((key) => String(selection[key] || "") !== String(selected[key] || ""))) return
      const sourceEntry = this.safeEntry
      const walkInCalendar = sourceEntry.walkInCalendar || {}
      if (!sourceEntry._id || !walkInCalendar.submittedAt) {
        this.$toast?.error("Der Gästebucheintrag konnte nicht eindeutig zugeordnet werden. Bitte öffne ihn erneut.")
        return
      }
      if (!walkInCalendar.appointment && !this.confirmAppointmentCreation([{ date: selection.date, start: selection.start }])) return

      const requestId = ++this.guestBookingRequestId
      this.linking = true
      this.guestBookingError = ""
      try {
        const result = await api.shiftManagement.linkGuestbookCustomer(sourceEntry._id, null, walkInCalendar.submittedAt, {
          withoutAccount: true,
          appointmentType: this.guestAppointmentType,
          date: selection.date,
          start: selection.start,
          end: selection.end,
          assignedEmployeeId: String(selection.colId),
        })
        if (requestId !== this.guestBookingRequestId) return
        this.$toast?.success("Gasttermin erstellt. Die Angaben aus dem Gästebuch wurden übernommen.")
        if (result?.assignmentWarning) this.$toast?.info(result.assignmentWarning)
        this.$emit("resolved", { leadId: sourceEntry._id, customer: null, appointment: result?.appointment || null, withoutAccount: true })
        this.guestStep = ""
        this.dialogOpen = false
      } catch (error) {
        if (requestId !== this.guestBookingRequestId) return
        const data = error?.response?.data || {}
        this.guestBookingError = data.message || "Der Gasttermin konnte nicht erstellt werden. Bitte versuche es erneut."
        if (error?.response?.status === 409 && String(data.code || "").startsWith("GUESTBOOK_")) {
          this.$toast?.error(this.guestBookingError)
          this.guestStep = ""
          this.$emit("refresh")
          this.$emit("input", false)
        } else if (error?.response?.status === 409) {
          this.guestFinderSelection = null
          this.$refs.guestAppointmentFinder?.refreshResults()
        }
      } finally {
        if (requestId === this.guestBookingRequestId) this.linking = false
      }
    },
    async linkCustomer(customer, sourceEntry = this.safeEntry) {
      if (this.linking) return
      const submittedAt = sourceEntry && sourceEntry.walkInCalendar && sourceEntry.walkInCalendar.submittedAt
      if (!customer || !customer._id || !sourceEntry || !sourceEntry._id || !submittedAt) {
        this.$toast?.error("Der Gästebucheintrag konnte nicht eindeutig zugeordnet werden. Bitte öffne ihn erneut.")
        return
      }

      const walkInCalendar = sourceEntry.walkInCalendar
      const assignment = sourceEntry.appointmentAssignment || {
        employeeId: this.appointmentTrainerId,
        employeeName: this.appointmentTrainerName,
        assignToCurrentUser: this.assignToCurrentUser,
      }
      if (!walkInCalendar.appointment && !this.confirmAppointmentCreation([{ date: walkInCalendar.date, start: walkInCalendar.time }])) return

      this.linking = true
      try {
        const result = await api.shiftManagement.linkGuestbookCustomer(sourceEntry._id, customer._id, submittedAt, {
          assignToCurrentUser: assignment.assignToCurrentUser,
          assignedEmployeeId: assignment.employeeId,
        })
        const linkedCustomer = result?.customer || customer
        const name = `${linkedCustomer.vorname || sourceEntry.name || ""} ${linkedCustomer.nachname || sourceEntry.surname || ""}`.trim()
        const employeeName = result?.assignedEmployeeName || assignment.employeeName
        const destination = result?.assignedToEmployee
          ? employeeName
            ? `${employeeName} als Termin zugewiesen`
            : "dem ausgewählten Trainer als Termin zugewiesen"
          : result?.assignedToCurrentUser
            ? "dir als Termin zugewiesen"
            : "als Termin unter MA Info angelegt"
        this.$toast?.success(`${name || "Der Kunde"} wurde verknüpft und ${destination}.`)
        if (result?.assignmentWarning) this.$toast?.info(result.assignmentWarning)
        this.$emit("resolved", { leadId: sourceEntry._id, customer: linkedCustomer, appointment: result?.appointment || null })
        this.dialogOpen = false
      } catch (error) {
        const message = error?.response?.data?.message || "Der Account konnte nicht verknüpft werden."
        this.$toast?.error(message)
        if (error?.response?.status === 409) {
          this.$emit("refresh")
          this.$emit("input", false)
        }
      } finally {
        this.linking = false
      }
    },
    async newAccountRegistered(registrationResult) {
      this.showRegisterDialog = false
      const sourceEntry = this.pendingRegistrationEntry
      this.pendingRegistrationEntry = null
      const customer = registrationResult?.user || registrationResult
      if (!customer || !customer._id) {
        this.$toast?.error("Der Account wurde erstellt, konnte aber nicht automatisch verknüpft werden.")
        return
      }
      await this.linkCustomer(customer, sourceEntry)
    },
  },
}
</script>

<style lang="scss" scoped>
.guestbook-finder {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.guestbook-finder__header {
  flex: 0 0 auto;
  border-bottom: 1px solid rgba(128, 128, 128, 0.2);
}

.guestbook-finder__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.guestbook-finder__body--saving {
  pointer-events: none;
}

.guestbook-dialog {
  --guestbook-surface: #fff;
  --guestbook-muted: #f6f8f8;
  --guestbook-border: #e2e8e6;
  --guestbook-text: #22312d;
  --guestbook-secondary: #66756f;
  --guestbook-accent: #087f70;
  border-radius: 18px !important;
  overflow: hidden;
}

.guestbook-hero {
  padding: 20px 24px;
  color: var(--guestbook-text);
  background: var(--guestbook-surface);
  border-bottom: 1px solid var(--guestbook-border);
}

.guestbook-hero__icon {
  width: 44px;
  height: 44px;
  flex: 0 0 auto;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 137, 123, 0.08);
}

.guestbook-eyebrow {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--guestbook-secondary);
}

.guestbook-title {
  margin: 3px 0 4px;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.3;
}

.guestbook-time {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: var(--guestbook-secondary);
}

.guestbook-content {
  background: var(--guestbook-surface);
}

.guestbook-person {
  display: flex;
  align-items: center;
}

.guestbook-avatar {
  flex: 0 0 auto;
  color: var(--guestbook-accent);
  font-size: 1rem;
  font-weight: 700;
  background: rgba(0, 137, 123, 0.08);
}

.guestbook-person__name {
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.guestbook-text {
  color: var(--guestbook-text);
}

.guestbook-assignment {
  border: 1px solid var(--guestbook-border);
  border-radius: 12px;
  overflow: hidden;
}

.guestbook-assignment__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
}

.guestbook-assignment__row + .guestbook-assignment__row {
  border-top: 1px solid var(--guestbook-border);
}

.guestbook-assignment__row--target {
  background: rgba(0, 137, 123, 0.045);
}

.guestbook-assignment__icon {
  flex: 0 0 auto;
}

.guestbook-assignment__label {
  color: var(--guestbook-secondary);
  font-size: 0.75rem;
  line-height: 1.4;
}

.guestbook-assignment__name {
  color: var(--guestbook-text);
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.guestbook-assignment__row--target .guestbook-assignment__name {
  color: var(--guestbook-accent);
}

.guestbook-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.guestbook-detail-card,
.guestbook-search-panel,
.guestbook-linked-panel {
  border-radius: 12px;
  border: 1px solid var(--guestbook-border);
  background: var(--guestbook-muted);
}

.guestbook-detail-card {
  padding: 14px;
}

.guestbook-detail-card__label {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  color: var(--guestbook-secondary);
  font-size: 0.75rem;
  font-weight: 500;
}

.guestbook-detail-card__value {
  color: var(--guestbook-text);
  font-weight: 600;
  line-height: 1.35;
}

.guestbook-section-icon {
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(0, 137, 123, 0.08);
}

.guestbook-dialog.theme--dark {
  --guestbook-surface: #1f2523;
  --guestbook-muted: #262e2b;
  --guestbook-border: #39443f;
  --guestbook-text: #eef3f0;
  --guestbook-secondary: #adbbb4;
  --guestbook-accent: #76cfbb;

  .guestbook-hero__icon .v-icon,
  .guestbook-section-icon .v-icon,
  .guestbook-assignment__row--target .v-icon {
    color: var(--guestbook-accent) !important;
  }
}

@media (max-width: 600px) {
  .guestbook-hero {
    padding: 20px;
  }

  .guestbook-hero__icon {
    width: 40px;
    height: 40px;
  }

  .guestbook-detail-grid {
    grid-template-columns: 1fr;
  }

  .guestbook-person {
    align-items: flex-start;
  }
}
</style>
